import { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Container, Dropdown, Form, Spinner, Table } from "react-bootstrap";
import { MoreHorizontal, RefreshCw, Search, Trash2 } from "lucide-react";
import { CallAPIDeletePromise, CallAPIGetPromise } from "Common/commaon";
import "./free-estimates.css";

type FreeEstimate = {
  id: number;
  renovation_type: string;
  budget_range: string;
  start_timeline: string;
  zip_code: string;
  city: string;
  state: string;
  location: string;
  email?: string;
  mobile?: string;
  user_id?: number;
  source?: string;
  status?: string;
  created_at?: string;
};

function formatDate(value?: string) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function normalizeEstimateResponse(response: any) {
  const body = response || {};
  const payload = Array.isArray(body)
    ? { data: body, stats: {} }
    : Array.isArray(body?.data)
      ? body
      : Array.isArray(body?.data?.data)
        ? body.data
        : Array.isArray(body?.result)
          ? { data: body.result, stats: body.stats }
          : { data: [], stats: body?.stats || body?.data?.stats || {} };

  return {
    rows: Array.isArray(payload.data) ? payload.data : [],
    stats: payload.stats || {},
  };
}

function formatPhone(value?: string) {
  if (!value) return "—";
  const digits = value.replace(/\D/g, "");
  const local = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  if (local.length !== 10) return value;
  return `(${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}`;
}

function formatLabel(value?: string) {
  if (!value) return "—";
  return value.replace(/_+/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

const PAGE_SIZE = 25;

export default function FreeEstimatesAdmin() {
  document.title = "Free Estimates";

  const [rows, setRows] = useState<FreeEstimate[]>([]);
  const [stats, setStats] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [error, setError] = useState("");
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const filteredRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return rows;

    return rows.filter((row) =>
      [
        row.renovation_type,
        row.budget_range,
        row.start_timeline,
        row.zip_code,
        row.city,
        row.state,
        row.location,
        row.email,
        row.mobile,
        row.source,
        row.status,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query))
    );
  }, [rows, search]);

  const loadEstimates = async () => {
    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams({ limit: "500", status });
      if (search.trim()) params.set("search", search.trim());
      const response = await CallAPIGetPromise(`/free-estimate/list?${params.toString()}`) as any
      const normalized = normalizeEstimateResponse(response);
      setRows(normalized.rows);
      setStats(normalized.stats);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Unable to load free estimate requests");
    } finally {
      setLoading(false);
    }
  };

  const deleteEstimate = async (estimate: FreeEstimate) => {
    const confirmed = window.confirm(`Delete free estimate request #${estimate.id}?`);
    if (!confirmed) return;

    setDeletingId(estimate.id);
    setError("");

    try {
      await CallAPIDeletePromise(`/free-estimate/${estimate.id}`);
      await loadEstimates();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Unable to delete free estimate request");
    } finally {
      setDeletingId(null);
    }
  };

  useEffect(() => {
    loadEstimates();
  }, [status]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, status]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const pageStart = (safePage - 1) * PAGE_SIZE;
  const visibleRows = filteredRows.slice(pageStart, pageStart + PAGE_SIZE);

  return (
    <div className="page-content free-estimates-page">
      <Container fluid>
        {error ? <Alert variant="danger">{error}</Alert> : null}

        <div className="free-estimate-stats">
          <Card><Card.Body><span>Total requests</span><strong>{Number(stats.total || rows.length || 0).toLocaleString()}</strong></Card.Body></Card>
          <Card><Card.Body><span>New</span><strong>{Number(stats.new_count ?? rows.filter((row) => (row.status || "NEW") === "NEW").length).toLocaleString()}</strong></Card.Body></Card>
          <Card><Card.Body><span>Matching results</span><strong>{filteredRows.length.toLocaleString()}</strong></Card.Body></Card>
        </div>

        <Card className="free-estimate-filters">
          <Card.Body>
            <div className="free-estimate-filter-grid">
              <label className="free-estimate-filter free-estimate-filter--search">
                <span>Search</span>
                <div className="app-search-field">
                  <Search className="app-field-icon" />
                  <Form.Control
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") loadEstimates();
                    }}
                    placeholder="Service, ZIP, email, or phone"
                  />
                </div>
              </label>
              <label className="free-estimate-filter">
                <span>Status</span>
                <Form.Select value={status} onChange={(event) => setStatus(event.target.value)}>
                  <option value="all">All statuses</option>
                  <option value="NEW">New</option>
                </Form.Select>
              </label>
              <Button variant="light" onClick={loadEstimates} disabled={loading}>
                {loading ? <Spinner size="sm" /> : <RefreshCw aria-hidden="true" />}
                Refresh
              </Button>
            </div>
          </Card.Body>
        </Card>

        <Card className="free-estimate-table-card">
          <Card.Body className="p-0">
            <div className="table-responsive">
              <Table hover className="free-estimate-table align-middle mb-0">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Service</th>
                    <th>Contact</th>
                    <th>Budget / Timeline</th>
                    <th>Location</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={8} className="text-center py-5">
                        <Spinner size="sm" className="me-2" />
                        Loading free estimates...
                      </td>
                    </tr>
                  ) : filteredRows.length ? (
                    visibleRows.map((row, index) => (
                      <tr key={row.id}>
                        <td>{pageStart + index + 1}</td>
                        <td>
                          <div className="fw-semibold">{formatLabel(row.renovation_type)}</div>
                        </td>
                        <td>
                          <div>{row.email || "—"}</div>
                          <div className="small text-muted">{formatPhone(row.mobile)}</div>
                        </td>
                        <td>
                          <div className="fw-semibold">{row.budget_range || "—"}</div>
                          <div className="small text-muted">{formatLabel(row.start_timeline)}</div>
                        </td>
                        <td>
                          <div>{row.location || [row.city, row.state, row.zip_code].filter(Boolean).join(", ") || "—"}</div>
                        </td>
                        <td>
                          <Badge bg={row.status === "NEW" ? "success" : "secondary"}>{formatLabel(row.status || "NEW")}</Badge>
                        </td>
                        <td>{formatDate(row.created_at)}</td>
                        <td className="text-end">
                          <Dropdown align="end">
                            <Dropdown.Toggle className="free-estimate-action" variant="light" size="sm" aria-label={`Actions for request ${row.id}`}>
                              {deletingId === row.id ? <Spinner size="sm" /> : <MoreHorizontal aria-hidden="true" />}
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                              <Dropdown.Item className="text-danger" onClick={() => deleteEstimate(row)} disabled={deletingId === row.id}>
                                <Trash2 aria-hidden="true" /> Delete request
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={8} className="text-center py-5 text-muted">
                        No free estimate requests found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </Card.Body>
          {!loading && filteredRows.length > 0 ? (
            <Card.Footer className="free-estimate-pagination">
              <span>Showing {pageStart + 1}–{Math.min(pageStart + PAGE_SIZE, filteredRows.length)} of {filteredRows.length}</span>
              <div>
                <Button size="sm" variant="light" disabled={safePage === 1} onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}>Previous</Button>
                <span>Page {safePage} of {totalPages}</span>
                <Button size="sm" variant="light" disabled={safePage === totalPages} onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}>Next</Button>
              </div>
            </Card.Footer>
          ) : null}
        </Card>
      </Container>
    </div>
  );
}
